import { useCallback, useEffect, useMemo, useState } from 'react';

import { TimelineDeleteChangeDuringDrag } from './types';
import { Clip } from '@/domains/asset';
import usePreviousEffect from '@/hooks/usePreviousEffect';
import { getAllCompositeSegments } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { getEndForSegment, getStartForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';

const DEFAULT_TRANSCRIPT_EXTENSION = {
  startTime: 0,
  startSegmentId: ''
};

export default function useTranscriptExtension({ clipData }: { clipData: Clip }) {
  const [transcriptExtension, setTranscriptExtension] = useState<
    | {
        startTime: number;
        startSegmentId: string;
      }
    | {
        endTime: number;
        endSegmentId: string;
      }
  >(DEFAULT_TRANSCRIPT_EXTENSION);

  const [dragExtension, setDragExtension] = useState<
    | {
        startTime: number;
        startSegmentId: string;
      }
    | {
        endTime: number;
        endSegmentId: string;
      }
    | null
  >(null);

  const compositeSegmentBoundsKey = useMemo(() => {
    const compositeSegments = getAllCompositeSegments(clipData);
    return compositeSegments
      .map(segment => `${segment.id}:${segment.currentBounds.fromFrame}-${segment.currentBounds.toFrame}`)
      .join('|');
  }, [clipData]);

  const onDeleteChangeDuringDrag = useCallback(
    ({ type, startTime, endTime, compositeSegmentId }: TimelineDeleteChangeDuringDrag) => {
      if (type === 'remove') {
        const segmentStartTime = getStartForSegment(clipData, compositeSegmentId);
        const segmentEndTime = getEndForSegment(clipData, compositeSegmentId);
        if (segmentStartTime >= startTime) {
          setDragExtension({
            startTime,
            startSegmentId: compositeSegmentId
          });
        } else if (segmentEndTime <= endTime) {
          setDragExtension({
            endTime,
            endSegmentId: compositeSegmentId
          });
        }
      }
      if (type === 'reset') {
        setDragExtension(null);
      }
    },
    [clipData]
  );

  useEffect(() => {
    setDragExtension(null);
    const eventListener = EventBus.on(CustomEvents.TimelineDeleteChangeDuringDrag, onDeleteChangeDuringDrag);
    return () => {
      EventBus.off(CustomEvents.TimelineDeleteChangeDuringDrag, eventListener);
    };
  }, [onDeleteChangeDuringDrag]);

  const resetTranscriptExtension = useCallback(() => {
    setTranscriptExtension(DEFAULT_TRANSCRIPT_EXTENSION);
  }, []);

  usePreviousEffect(compositeSegmentBoundsKey, resetTranscriptExtension);

  return {
    transcriptExtension: dragExtension ? dragExtension : transcriptExtension,
    setTranscriptExtension
  };
}
